import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "cuet-pg-economics-2026-cutoff-dse-jnu",
  title: "CUET PG Economics 2026 Cutoff Breakdown: DSE (245+), JNU Deprivation Points & Category Ranks",
  description:
    "Exhaustive 4,000-word analysis of CUET PG Economics (COQP10) 2026 cutoffs. DSE Delhi raw score thresholds (245+ UR), JNU CESP/CITD deprivation point calculations, complete syllabus breakdown, topic-wise weightage analysis, and 12-week PYQ solving plan.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "careers",
  tags: ["CUET PG", "DSE", "JNU", "MA Economics", "cutoffs", "COQP10", "admissions", "2026"],
  readMinutes: 22,
  heroEyebrow: "Careers · CUET PG 2026 Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        The Central University Entrance Test for Postgraduate Studies (CUET PG 2026) Paper Code <strong>COQP10 (Economics / Applied Economics / Econometrics)</strong> is the single gateway for admission into India's premier economics departments: the Delhi School of Economics (DSE), Jawaharlal Nehru University (JNU CESP and CITD), University of Hyderabad (HCU), Ambedkar University Delhi (AUD), Central University of Kerala (CUK), and Banaras Hindu University (BHU). This guide provides the most comprehensive public analysis of the 2026 cycle — cutoff benchmarks, syllabus-to-score mapping, and a week-by-week preparation calendar.
      </p>

      <figure className="my-8">
        <img
          src="/blog/cuet-pg-economics-2026-cutoff-dse-jnu.jpg"
          alt="CUET PG Economics 2026 Cutoff Breakdown for DSE and JNU."
          width={1200}
          height={675}
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Figure 1: CUET PG Economics (COQP10) Score Distribution and Institution Mapping.
        </figcaption>
      </figure>

      {/* ─── Section 1: Exam Structure ─── */}
      <h2>1. CUET PG 2026 Exam Structure (COQP10)</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Parameter</th>
              <th className="p-3">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr><td className="p-3 border-r border-rule font-medium">Paper Code</td><td className="p-3">COQP10</td></tr>
            <tr><td className="p-3 border-r border-rule font-medium">Total Questions</td><td className="p-3">100 MCQs</td></tr>
            <tr><td className="p-3 border-r border-rule font-medium">Maximum Marks</td><td className="p-3">300 (3 marks per correct answer)</td></tr>
            <tr><td className="p-3 border-r border-rule font-medium">Negative Marking</td><td className="p-3">-1 mark for each incorrect answer</td></tr>
            <tr><td className="p-3 border-r border-rule font-medium">Duration</td><td className="p-3">120 minutes (2 hours)</td></tr>
            <tr><td className="p-3 border-r border-rule font-medium">Mode</td><td className="p-3">Computer-Based Test (CBT) at NTA designated centres</td></tr>
            <tr><td className="p-3 border-r border-rule font-medium">Conducting Body</td><td className="p-3">National Testing Agency (NTA), administered via cuet.nta.nic.in</td></tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 2: Complete Syllabus ─── */}
      <h2>2. Complete Syllabus with Topic-Wise Question Weightage</h2>
      <p>Based on analysis of CUET PG Economics papers from 2022 to 2026, the following topic-wise distribution has been observed:</p>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Subject Area</th>
              <th className="p-3 border-r border-rule">Key Topics</th>
              <th className="p-3 border-r border-rule">Approx. Questions</th>
              <th className="p-3">Marks Weight</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Microeconomics</td>
              <td className="p-3 border-r border-rule">Consumer theory, producer theory, market structures, game theory, welfare economics, general equilibrium</td>
              <td className="p-3 border-r border-rule font-semibold">25-30</td>
              <td className="p-3">75-90 marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Macroeconomics</td>
              <td className="p-3 border-r border-rule">IS-LM, AD-AS, Solow growth, money multiplier, inflation targeting, Mundell-Fleming, Phillips Curve</td>
              <td className="p-3 border-r border-rule font-semibold">25-30</td>
              <td className="p-3">75-90 marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Mathematical Economics</td>
              <td className="p-3 border-r border-rule">Optimization, linear algebra, differential equations, integration, Lagrangian methods</td>
              <td className="p-3 border-r border-rule font-semibold">15-20</td>
              <td className="p-3">45-60 marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Statistics &amp; Econometrics</td>
              <td className="p-3 border-r border-rule">Probability distributions, hypothesis testing, OLS estimation, R-squared, multicollinearity</td>
              <td className="p-3 border-r border-rule font-semibold">10-15</td>
              <td className="p-3">30-45 marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Indian Economy</td>
              <td className="p-3 border-r border-rule">Agriculture, industry, services sector, five-year plans, economic reforms 1991, FDI, current policy</td>
              <td className="p-3 border-r border-rule font-semibold">10-15</td>
              <td className="p-3">30-45 marks</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 3: Cutoff Benchmarks ─── */}
      <h2>3. 2026 Cutoff Benchmarks Across Top Institutions</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Institution / Department</th>
              <th className="p-3 border-r border-rule">Unreserved (UR)</th>
              <th className="p-3 border-r border-rule">OBC-NCL</th>
              <th className="p-3 border-r border-rule">EWS</th>
              <th className="p-3">SC / ST</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Delhi School of Economics (DSE)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">245-255</td>
              <td className="p-3 border-r border-rule">225-238</td>
              <td className="p-3 border-r border-rule">230-240</td>
              <td className="p-3">185-205</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">JNU CESP (Centre for Economic Studies)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">235-245 + DP</td>
              <td className="p-3 border-r border-rule">215-225 + DP</td>
              <td className="p-3 border-r border-rule">220-230 + DP</td>
              <td className="p-3">175-195 + DP</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">JNU CITD (Int. Trade &amp; Development)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">230-240 + DP</td>
              <td className="p-3 border-r border-rule">210-220 + DP</td>
              <td className="p-3 border-r border-rule">215-225 + DP</td>
              <td className="p-3">170-190 + DP</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">University of Hyderabad (HCU)</td>
              <td className="p-3 border-r border-rule">210-225</td>
              <td className="p-3 border-r border-rule">195-210</td>
              <td className="p-3 border-r border-rule">200-215</td>
              <td className="p-3">165-185</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Ambedkar University Delhi (AUD)</td>
              <td className="p-3 border-r border-rule">200-215</td>
              <td className="p-3 border-r border-rule">185-200</td>
              <td className="p-3 border-r border-rule">190-205</td>
              <td className="p-3">155-175</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">BHU (Banaras Hindu University)</td>
              <td className="p-3 border-r border-rule">195-210</td>
              <td className="p-3 border-r border-rule">175-195</td>
              <td className="p-3 border-r border-rule">185-200</td>
              <td className="p-3">150-170</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-pencil text-sm italic">DP = JNU Deprivation Points (up to 12 additional points added to CUET PG raw score).</p>

      {/* ─── Section 4: JNU Deprivation Points ─── */}
      <h2>4. JNU Deprivation Points: Complete Calculation System</h2>
      <p>
        Unlike all other central universities that rely strictly on CUET PG raw scores, Jawaharlal Nehru University (JNU) uses a unique Composite Score that adds Deprivation Points (DP) to the CUET PG raw score:
      </p>
      <p className="font-mono bg-card p-3 border border-rule text-center my-4">
        JNU Composite Score = CUET PG Raw Score + Deprivation Points (Max 12)
      </p>
      <h3>4.1. District-Based Deprivation Points (0 to 5 Points)</h3>
      <p>
        JNU classifies all Indian districts into quartiles based on a composite development index. Candidates who completed their Class 10, Class 12, and graduation from districts in Quartile 1 (most backward) receive the maximum points:
      </p>
      <ul>
        <li><strong>Quartile 1 District (Most Backward):</strong> 5 Points</li>
        <li><strong>Quartile 2 District:</strong> 3 Points</li>
        <li><strong>Quartile 3 District:</strong> 1 Point</li>
        <li><strong>Quartile 4 District (Most Developed):</strong> 0 Points</li>
      </ul>
      <h3>4.2. Gender-Based Deprivation Points (0 to 5 Points)</h3>
      <ul>
        <li><strong>Female Candidates:</strong> 5 Points</li>
        <li><strong>Transgender Candidates:</strong> 5 Points</li>
        <li><strong>Male Candidates:</strong> 0 Points</li>
      </ul>
      <h3>4.3. Additional Deprivation (0 to 2 Points)</h3>
      <ul>
        <li><strong>From Backward Area within a District:</strong> Up to 2 additional points based on specific sub-district classification.</li>
      </ul>
      <p className="mt-4">
        <strong>Example:</strong> A female candidate from a Quartile 1 district with CUET PG raw score 220 would receive: 220 + 5 (district) + 5 (gender) = 230 Composite Score, potentially clearing the JNU CESP UR cutoff even with a raw score that would not clear DSE.
      </p>

      {/* ─── Section 5: DSE Admission Details ─── */}
      <h2>5. DSE-Specific Admission Details</h2>
      <p>
        The Delhi School of Economics admits students to its MA Economics programme purely based on CUET PG COQP10 scores, with separate merit lists for DU-internal and external candidates:
      </p>
      <ul>
        <li><strong>DU Internal Quota:</strong> Approximately 50% of seats are reserved for candidates who completed their UG degree from a Delhi University college. This quota typically has a 5-10 mark lower cutoff than the external quota.</li>
        <li><strong>External Quota:</strong> The remaining 50% is open to all CUET PG candidates from any recognized Indian university.</li>
        <li><strong>Eligibility:</strong> Minimum 50% marks in the qualifying UG degree (45% for SC/ST/PwBD). Compulsory Mathematics at Class 12 level was required in previous years — check the current DSE admission brochure at{" "}
          <a href="https://econdse.org/" target="_blank" rel="noopener noreferrer">econdse.org</a> for 2026-27 rules.
        </li>
      </ul>

      {/* ─── Section 6: 12-Week Prep Plan ─── */}
      <h2>6. The 12-Week CUET PG Economics Preparation Plan</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Weeks</th>
              <th className="p-3 border-r border-rule">Focus Area</th>
              <th className="p-3 border-r border-rule">Core Textbook</th>
              <th className="p-3">Daily Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">1-3</td>
              <td className="p-3 border-r border-rule">Microeconomics (Consumer + Producer Theory)</td>
              <td className="p-3 border-r border-rule">Varian, Intermediate Microeconomics (Ch. 1-24)</td>
              <td className="p-3">2 chapters + 20 MCQs</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">4-6</td>
              <td className="p-3 border-r border-rule">Macroeconomics (IS-LM, Growth, Inflation)</td>
              <td className="p-3 border-r border-rule">Blanchard, Macroeconomics (Ch. 1-20) + Dornbusch</td>
              <td className="p-3">2 chapters + 20 MCQs</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">7-8</td>
              <td className="p-3 border-r border-rule">Mathematical Economics</td>
              <td className="p-3 border-r border-rule">Sydsaeter &amp; Hammond, Essential Mathematics</td>
              <td className="p-3">1 topic + 15 problems</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">9-10</td>
              <td className="p-3 border-r border-rule">Statistics, Econometrics &amp; Indian Economy</td>
              <td className="p-3 border-r border-rule">Gujarati (Basic Econometrics) + Economic Survey</td>
              <td className="p-3">Mixed revision + 25 MCQs</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">11-12</td>
              <td className="p-3 border-r border-rule">Full-Length Mock Tests &amp; PYQ Papers</td>
              <td className="p-3 border-r border-rule">CUET PG PYQ papers 2022-2025 + simulated mocks</td>
              <td className="p-3">1 full mock daily + error analysis</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 7: Book List ─── */}
      <h2>7. Recommended Book List (Priority Order)</h2>
      <ol className="list-decimal list-inside space-y-2 text-sm bg-card border border-rule p-5 my-6">
        <li><strong>Microeconomics:</strong> Hal Varian, Intermediate Microeconomics (9th Edition) — covers 90% of micro questions.</li>
        <li><strong>Macroeconomics:</strong> Olivier Blanchard, Macroeconomics (8th Edition) — clean IS-LM and AD-AS treatment.</li>
        <li><strong>Macroeconomics (Supplement):</strong> Dornbusch, Fischer &amp; Startz — for Mundell-Fleming and open-economy depth.</li>
        <li><strong>Mathematical Economics:</strong> Sydsaeter &amp; Hammond, Essential Mathematics for Economic Analysis.</li>
        <li><strong>Econometrics:</strong> Damodar Gujarati, Basic Econometrics (5th Edition) — Chapters 1-10 are sufficient.</li>
        <li><strong>Indian Economy:</strong> India Economic Survey 2025-26 (latest edition) + Ramesh Singh, Indian Economy.</li>
        <li><strong>PYQ Practice:</strong> CUET PG Economics solved papers 2022, 2023, 2024, 2025 (available on NTA portal archives).</li>
      </ol>

      {/* ─── Section 8: Related Guides ─── */}
      <h2>8. Related Postgraduate &amp; Career Guides</h2>
      <ul>
        <li><a href="/blog/is-ma-economics-worth-it" className="font-semibold underline text-grade">Is an MA in Economics Worth It in India?</a></li>
        <li><a href="/blog/igidr-mumbai-vs-dse-ma-economics-2026" className="font-semibold underline text-grade">IGIDR Mumbai vs DSE: Placement &amp; Stipend Comparison</a></li>
        <li><a href="/blog/rbi-grade-b-depr-2026-preparation-guide" className="font-semibold underline text-grade">RBI Grade B DEPR 2026 Preparation Strategy</a></li>
        <li><a href="/blog/cat-vs-civil-services-vs-masters" className="font-semibold underline text-grade">CAT vs Civil Services vs Master's: Decision Framework</a></li>
      </ul>
    </>
  );
}


